/**
 * MatchConsensusPanel — fires the multi-AI consensus check on a Match
 * and shows per-provider responses + agreement score.
 *
 * Use case: high-stakes match decisions ("should we pursue this lead?").
 * One panel button → backend fans the same prompt to every configured
 * AI provider (Anthropic + OpenAI + Grok + DeepSeek + Gemini),
 * computes TF-IDF cosine agreement, returns a verdict.
 *
 * The agreement score (0.0–1.0) is the load-bearing signal:
 *   ≥ 0.7  → strong consensus, take the majority answer
 *   0.4-0.7 → mixed signals, operator should read the responses
 *   < 0.4  → models disagree, escalate to human review
 *
 * Wired into MatchDetailPage right under MatchOutreachPanel so the
 * "advance status → check consensus → send intro" flow is linear.
 */
import React, { useState } from 'react';
import {
  Brain, Loader2, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle2, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import aiSettingsService, {
  ConsensusResult,
} from '../services/aiSettingsService';
import { Match } from '../types/match';

interface Props {
  match: Match;
}

const formatPct = (score: number | null): string => {
  if (score === null) return '—';
  return `${Math.round(score * 100)}%`;
};

const agreementClass = (score: number | null): string => {
  if (score === null) return 'text-gray-500';
  if (score >= 0.7) return 'text-emerald-700 bg-emerald-50';
  if (score >= 0.4) return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
};

const agreementLabel = (score: number | null): string => {
  if (score === null) return 'No agreement (insufficient responses)';
  if (score >= 0.7) return 'Strong consensus';
  if (score >= 0.4) return 'Mixed signals';
  return 'Models disagree — escalate';
};

const MatchConsensusPanel: React.FC<Props> = ({ match }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ConsensusResult | null>(null);
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(
    new Set(),
  );

  const buildPrompt = (): string => {
    const investorName =
      match.investor?.firm_name
        ? `${match.investor.full_name} (${match.investor.firm_name})`
        : match.investor?.full_name || 'the investor';
    const startupName = match.startup?.name || 'the startup';
    const score = match.overall_score ?? 0;
    const dimensions = match.dimension_scores
      ? Object.entries(match.dimension_scores)
          .filter(([, v]) => typeof v === 'number')
          .map(([k, v]) => `${k}=${v}`)
          .join(', ')
      : '';
    const talking = (match.talking_points || []).slice(0, 3).join('; ');

    return [
      `Evaluate this investor-startup match and answer in 2-3 sentences:`,
      `Should ${investorName} pursue ${startupName}?`,
      `Overall fit score: ${score}/100.`,
      dimensions ? `Dimension scores: ${dimensions}.` : '',
      talking ? `Talking points: ${talking}.` : '',
      `Be concise. Your answer should explain the verdict in plain English.`,
    ].filter(Boolean).join('\n');
  };

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    const prompt = buildPrompt();
    const r = await aiSettingsService.runConsensus({
      prompt,
      max_tokens: 400,
    });
    setRunning(false);
    if (r.error) {
      toast.error(r.error);
      return;
    }
    if (r.data) {
      setResult(r.data);
      const score = r.data.agreement;
      const succeeded = r.data.succeeded?.length ?? 0;
      // No agreement score available — usually because fewer than 2
      // providers responded. That's a configuration issue, not a model
      // disagreement, so route the user to /integrations instead of
      // showing the misleading "models disagree" toast.
      if (score === null) {
        if (succeeded < 2) {
          toast(
            `Only ${succeeded} provider${succeeded === 1 ? '' : 's'} responded. ` +
              `Configure additional AI providers (Anthropic, OpenAI, Grok, ` +
              `DeepSeek, Gemini) in /integrations to enable consensus scoring.`,
            { icon: 'ℹ️', duration: 9000 },
          );
        } else {
          toast(
            'Consensus inconclusive — check responses below.',
            { icon: 'ℹ️' },
          );
        }
      } else if (score >= 0.7) {
        toast.success(`Strong consensus (${formatPct(score)})`);
      } else if (score >= 0.4) {
        toast(`Mixed signals (${formatPct(score)})`, { icon: '⚠️' });
      } else {
        toast.error(
          `Models disagree (${formatPct(score)}) — review responses`,
        );
      }
    }
  };

  const toggleProvider = (name: string) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-start gap-3 mb-4">
        <Brain className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">
            Multi-AI consensus check
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Fans the match summary to every configured AI provider
            (Anthropic / OpenAI / Grok / DeepSeek / Gemini) and scores
            inter-model agreement. Use for high-stakes decisions where
            single-model output isn't enough confidence.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Need at least 2 providers configured in
            <a href="/integrations" className="underline ml-1">/integrations</a> for an agreement score.
          </p>
        </div>
      </div>

      {!result && (
        <button
          type="button"
          onClick={handleRun}
          disabled={running}
          className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {running ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Brain className="w-4 h-4 mr-2" />
          )}
          {running ? 'Querying providers...' : 'Run consensus check'}
        </button>
      )}

      {result && (
        <div className="space-y-4">
          {/* Headline verdict */}
          <div
            className={`rounded-lg px-4 py-3 ${agreementClass(result.agreement)}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide opacity-80">
                  Agreement score
                </div>
                <div className="text-2xl font-bold">
                  {formatPct(result.agreement)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide opacity-80">
                  Status
                </div>
                <div className="text-sm font-semibold">
                  {agreementLabel(result.agreement)}
                </div>
              </div>
            </div>
          </div>

          {/* Per-provider responses */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center justify-between">
              <span>Provider responses</span>
              <span className="text-gray-400 normal-case font-normal">
                {result.succeeded.length}/{result.succeeded.length + result.failed.length} responded
              </span>
            </div>
            {Object.entries(result.responses).map(([name, response]) => {
              const isOpen = expandedProviders.has(name);
              const failed = response === null;
              return (
                <div
                  key={name}
                  className={`border rounded-lg ${
                    failed ? 'border-gray-200 bg-gray-50' : 'border-gray-200'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => !failed && toggleProvider(name)}
                    disabled={failed}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
                      failed ? 'cursor-default' : 'hover:bg-gray-50'
                    }`}
                  >
                    {failed ? (
                      <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : isOpen ? (
                      <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    )}
                    <span className="font-medium text-sm capitalize text-gray-900">
                      {name}
                    </span>
                    {failed ? (
                      <span className="text-xs text-gray-500 ml-auto">
                        no response (key not configured / error)
                      </span>
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto" />
                    )}
                  </button>
                  {isOpen && response && (
                    <div className="px-3 pb-3 pt-1 text-sm text-gray-700 whitespace-pre-wrap border-t border-gray-100">
                      {response}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Majority answer */}
          {result.majority && (
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                Majority answer
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap">
                {result.majority}
              </div>
            </div>
          )}

          {result.agreement !== null && result.agreement < 0.4 && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Low agreement — the models gave substantially different answers.
                Read each response below before acting on the majority.
              </span>
            </div>
          )}

          <div className="pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleRun}
              disabled={running}
              className="text-xs text-purple-600 hover:text-purple-700 inline-flex items-center gap-1 disabled:opacity-50"
            >
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Re-run consensus
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default MatchConsensusPanel;
